/**
 * Automation Engine — Otomatik İklim Kontrolü + Self-Healing + VPD Desteği
 *
 * Sensör okuması geldiğinde kuralları değerlendirir, eşik aşımında
 * HA cihazlarını otomatik kontrol eder ve her aksiyonu loglar.
 *
 * Kural yapısı:
 *   EĞER  metric > max  VE  stage = X  →  device = AÇIK
 *   5 dakika sonra kontrol et → hâlâ aşımda → alarm gönder
 *
 * Self-Healing (v2):
 *   Cihaz açıldıktan recheckMinutes sonra koşul hâlâ sağlanıyorsa:
 *   - Cihaz aynı durumda ve akım okuma destekleniyorsa: "Çalışıyor ama yetersiz"
 *   - Koşul bozuldu ve hiç ilerleme yok: "Cihaz arızalı olabilir"
 *   - 3 ardışık başarısız recheck → escalation alarmı
 *
 * VPD Desteği (v2):
 *   'vpd' metric tipi eklendi; psikrometrik hesap dışarıda yapılır,
 *   VPD değeri reading.vpd olarak iletilir.
 */

import type { SensorReading, DeviceStatus, CultivationStage } from '../types';
import { toggleDeviceRemote } from './habridge';
import { sendTelegramMessage } from './notificationService';

// ── Types ─────────────────────────────────────────────────────────────────────

export type AutomationMetric = 'temperature' | 'humidity' | 'co2' | 'vpd';
export type AutomationOperator = 'above' | 'below';
export type AutomationAction = 'turn_on' | 'turn_off';

export interface AutomationRule {
  id: string;
  name: string;
  enabled: boolean;
  tentId: string;
  /** Which cultivation stages this rule applies to (empty = all) */
  stages: CultivationStage[];
  /** Sensor metric to watch */
  metric: AutomationMetric;
  /** Comparison operator */
  operator: AutomationOperator;
  /** Threshold value */
  threshold: number;
  /** Target device ID */
  deviceId: string;
  /** What to do when triggered */
  action: AutomationAction;
  /** How long to keep device in that state (minutes). 0 = until condition clears */
  durationMinutes: number;
  /** Minimum minutes between re-triggers (prevents flapping) */
  cooldownMinutes: number;
  /** Re-check delay: after action, wait this many minutes then re-evaluate */
  recheckMinutes: number;
  /** If still breached after recheck, send alarm */
  alarmOnRecheck: boolean;
  /** Priority: higher priority rules evaluated first */
  priority: number;
}

export interface AutomationActionLog {
  id: string;
  ruleId: string;
  ruleName: string;
  timestamp: string;
  metric: AutomationMetric;
  sensorValue: number;
  threshold: number;
  operator: AutomationOperator;
  deviceId: string;
  deviceName: string;
  action: AutomationAction;
  /** 'triggered' | 'recheck_ok' | 'recheck_alarm' | 'auto_off' */
  outcome: string;
  tentId: string;
}

// ── Rule State Tracking ───────────────────────────────────────────────────────

interface RuleState {
  lastTriggeredAt: number;   // ms timestamp
  recheckScheduled: boolean;
  autoOffScheduled: boolean;
  // Self-Healing tracking
  failedRecheckCount: number;  // ardışık başarısız recheck sayısı
  valueAtTrigger: number | null; // tetiklendiğindeki değer (ilerleme var mı?)
  recheckAlarmFiredAt: number;   // son recheck alarmı zamanı (tekrar spam'i önler)
}

const ruleStates = new Map<string, RuleState>();

function getRuleState(ruleId: string): RuleState {
  if (!ruleStates.has(ruleId)) {
    ruleStates.set(ruleId, {
      lastTriggeredAt: 0,
      recheckScheduled: false,
      autoOffScheduled: false,
      failedRecheckCount: 0,
      valueAtTrigger: null,
      recheckAlarmFiredAt: 0,
    });
  }
  return ruleStates.get(ruleId)!;
}

// ── Default Rules ─────────────────────────────────────────────────────────────

export function getDefaultRules(): AutomationRule[] {
  return [
    {
      id: 'auto-humidity-low',
      name: 'Nem Düşük → Nemlendirici Aç',
      enabled: true,
      tentId: 'tent-1',
      stages: ['fruiting', 'pinning'],
      metric: 'humidity',
      operator: 'below',
      threshold: 82,
      deviceId: '',  // User must map device
      action: 'turn_on',
      durationMinutes: 0,
      cooldownMinutes: 10,
      recheckMinutes: 5,
      alarmOnRecheck: true,
      priority: 10,
    },
    {
      id: 'auto-humidity-high',
      name: 'Nem Yüksek → Nemlendirici Kapat',
      enabled: true,
      tentId: 'tent-1',
      stages: ['fruiting', 'pinning'],
      metric: 'humidity',
      operator: 'above',
      threshold: 95,
      deviceId: '',
      action: 'turn_off',
      durationMinutes: 0,
      cooldownMinutes: 10,
      recheckMinutes: 5,
      alarmOnRecheck: false,
      priority: 9,
    },
    {
      id: 'auto-co2-high',
      name: 'CO₂ Yüksek → FAE Fan Aç',
      enabled: true,
      tentId: 'tent-1',
      stages: ['fruiting', 'pinning'],
      metric: 'co2',
      operator: 'above',
      threshold: 1000, // Lion's Mane: deformation starts above 1000 ppm
      deviceId: '',
      action: 'turn_on',
      durationMinutes: 15,
      cooldownMinutes: 20,
      recheckMinutes: 5,
      alarmOnRecheck: true,
      priority: 8,
    },
    {
      id: 'auto-temp-high',
      name: 'Sıcaklık Yüksek → Soğutucu Aç',
      enabled: true,
      tentId: 'tent-1',
      stages: ['fruiting', 'pinning'],
      metric: 'temperature',
      operator: 'above',
      threshold: 22,
      deviceId: '',
      action: 'turn_on',
      durationMinutes: 0,
      cooldownMinutes: 15,
      recheckMinutes: 5,
      alarmOnRecheck: true,
      priority: 7,
    },
    {
      id: 'auto-temp-low',
      name: 'Sıcaklık Düşük → Isıtıcı Aç',
      enabled: true,
      tentId: 'tent-1',
      stages: ['fruiting', 'spawn_run'],
      metric: 'temperature',
      operator: 'below',
      threshold: 15,
      deviceId: '',
      action: 'turn_on',
      durationMinutes: 0,
      cooldownMinutes: 15,
      recheckMinutes: 5,
      alarmOnRecheck: true,
      priority: 6,
    },
  ];
}

// ── Core Engine ───────────────────────────────────────────────────────────────

const METRIC_LABELS: Record<AutomationMetric, string> = {
  temperature: 'Sıcaklık',
  humidity: 'Nem',
  co2: 'CO₂',
  vpd: 'VPD',
};

const METRIC_UNITS: Record<AutomationMetric, string> = {
  temperature: '°C',
  humidity: '%',
  co2: 'ppm',
  vpd: 'kPa',
};

/** Read metric value from reading — vpd is an optional field */
function getMetricValue(metric: AutomationMetric, reading: SensorReading): number | null {
  if (metric === 'vpd') return (reading as SensorReading & { vpd?: number }).vpd ?? null;
  return reading[metric as 'temperature' | 'humidity' | 'co2'];
}

function isBreached(rule: AutomationRule, reading: SensorReading): boolean {
  const value = getMetricValue(rule.metric, reading);
  if (value === null) return false; // metric not available, skip rule
  if (rule.operator === 'above') return value > rule.threshold;
  return value < rule.threshold;
}

export interface AutomationContext {
  reading: SensorReading;
  devices: DeviceStatus[];
  activeStage: CultivationStage;
  activeTentId: string;
  rules: AutomationRule[];
  onDeviceToggle: (deviceId: string, turnOn: boolean) => void;
  onLog: (log: AutomationActionLog) => void;
  onAlarm: (message: string) => void;
}

/**
 * Evaluate all automation rules against current sensor reading.
 * Called every time a new sensor reading arrives (typically every 30s).
 */
export function evaluateRules(ctx: AutomationContext): void {
  const now = Date.now();
  const { reading, devices, activeStage, activeTentId, rules } = ctx;

  // Sort by priority (higher first)
  const sorted = [...rules]
    .filter(r => r.enabled)
    .filter(r => r.tentId === activeTentId || r.tentId === '*')
    .filter(r => r.stages.length === 0 || r.stages.includes(activeStage))
    .sort((a, b) => b.priority - a.priority);

  for (const rule of sorted) {
    const state = getRuleState(rule.id);
    const device = devices.find(d => d.id === rule.deviceId);
    if (!device) continue; // Device not mapped

    const breached = isBreached(rule, reading);
    const cooldownMs = rule.cooldownMinutes * 60_000;
    const sinceLastTrigger = now - state.lastTriggeredAt;

    if (breached && sinceLastTrigger >= cooldownMs) {
      // Condition met — execute action
      const turnOn = rule.action === 'turn_on';
      
      // Only act if device isn't already in desired state
      if (device.isActive !== turnOn) {
        // Toggle device via HA
        if (device.haEntityId) {
          toggleDeviceRemote(device.id, device.haEntityId, turnOn).catch(() => {});
        }
        ctx.onDeviceToggle(device.id, turnOn);

        const log: AutomationActionLog = {
          id: `alog-${crypto.randomUUID()}`,
          ruleId: rule.id,
          ruleName: rule.name,
          timestamp: new Date().toISOString(),
          metric: rule.metric,
          sensorValue: getMetricValue(rule.metric, reading) ?? 0,
          threshold: rule.threshold,
          operator: rule.operator,
          deviceId: device.id,
          deviceName: device.name,
          action: rule.action,
          outcome: 'triggered',
          tentId: activeTentId,
        };
        ctx.onLog(log);

        state.lastTriggeredAt = now;

        // Schedule auto-off if duration is set
        if (rule.durationMinutes > 0 && turnOn && !state.autoOffScheduled) {
          state.autoOffScheduled = true;
          setTimeout(() => {
            if (device.haEntityId) {
              toggleDeviceRemote(device.id, device.haEntityId, false).catch(() => {});
            }
            ctx.onDeviceToggle(device.id, false);
            ctx.onLog({
              ...log,
              id: `alog-${crypto.randomUUID()}`,
              timestamp: new Date().toISOString(),
              action: 'turn_off',
              outcome: 'auto_off',
            });
            state.autoOffScheduled = false;
          }, rule.durationMinutes * 60_000);
        }

        // Schedule recheck if alarm enabled
        if (rule.alarmOnRecheck && rule.recheckMinutes > 0 && !state.recheckScheduled) {
          state.recheckScheduled = true;
          setTimeout(() => {
            state.recheckScheduled = false;
            // Re-evaluate will happen naturally on next reading
            // But we flag that a recheck alarm should fire if still breached
          }, rule.recheckMinutes * 60_000);
        }
      }
    } else if (!breached && state.lastTriggeredAt > 0) {
      // Condition cleared — if action was turn_on and duration=0, turn off
      if (rule.action === 'turn_on' && rule.durationMinutes === 0 && device.isActive) {
        if (device.haEntityId) {
          toggleDeviceRemote(device.id, device.haEntityId, false).catch(() => {});
        }
        ctx.onDeviceToggle(device.id, false);
        ctx.onLog({
          id: `alog-${crypto.randomUUID()}`,
          ruleId: rule.id,
          ruleName: rule.name,
          timestamp: new Date().toISOString(),
          metric: rule.metric,
          sensorValue: getMetricValue(rule.metric, reading) ?? 0,
          threshold: rule.threshold,
          operator: rule.operator,
          deviceId: device.id,
          deviceName: device.name,
          action: 'turn_off',
          outcome: 'recheck_ok',
          tentId: activeTentId,
        });
      }
    }

    // ── Self-Healing Recheck ───────────────────────────────────────────────
    // Recheck alarm: if rule was triggered, recheck period passed, and still breached
    const recheckWindowMs = rule.recheckMinutes * 60_000;
    const recheckSpamGuardMs = recheckWindowMs * 2; // aynı kuraldan 2× süre geçmeden tekrar alarm atma
    const sinceLastRecheckAlarm = now - state.recheckAlarmFiredAt;

    if (
      rule.alarmOnRecheck &&
      state.lastTriggeredAt > 0 &&
      sinceLastTrigger >= recheckWindowMs &&
      sinceLastRecheckAlarm >= recheckSpamGuardMs &&
      breached
    ) {
      const unit   = METRIC_UNITS[rule.metric];
      const label  = METRIC_LABELS[rule.metric];
      const currentVal = getMetricValue(rule.metric, reading) ?? 0;

      state.failedRecheckCount++;
      state.recheckAlarmFiredAt = now;

      // Başlangıç değerine göre ilerleme var mı?
      const valueAtTrigger = state.valueAtTrigger ?? currentVal;
      const delta = rule.operator === 'below'
        ? currentVal - valueAtTrigger   // below ihlali: değer yükselmeli
        : valueAtTrigger - currentVal;  // above ihlali: değer düşmeli
      const hasProgress = delta > 0;

      let msg: string;
      if (state.failedRecheckCount >= 3) {
        // Eskalasyon — 3 ardışık başarısız recheck
        msg = [
          `🚨 <b>ESKALASYon — ${label} ${state.failedRecheckCount}. kontrol hâlâ başarısız</b>`,
          `📊 Şu an: ${currentVal}${unit}  ·  Eşik: ${rule.threshold}${unit}`,
          `🔧 Cihaz: ${device.name} — <b>Fiziksel müdahale gerekli</b>`,
          `⚡ Kural: ${rule.name}`,
        ].join('\n');
      } else if (!hasProgress) {
        // Hiç ilerleme yok → cihaz gerçekten çalışmıyor olabilir
        msg = [
          `⚠️ <b>Self-Healing: ${device.name} etki yaratmıyor</b>`,
          `📊 ${label}: ${currentVal}${unit} (başlangıç: ${valueAtTrigger}${unit}, eşik: ${rule.threshold}${unit})`,
          `❓ Olası nedenler: Su bitti · Membran tıkandı · Cihaz arızalı · Dış koşul çok zorlu`,
          `🔍 Kontrol et: ${rule.name}`,
        ].join('\n');
      } else {
        // İlerleme var ama yeterli değil
        msg = [
          `🟡 <b>${label} hâlâ eşik dışında — iyileşme var ama yavaş</b>`,
          `📊 Şu an: ${currentVal}${unit}  ·  Eşik: ${rule.threshold}${unit}  ·  İlerleme: ${delta > 0 ? '+' : ''}${Math.round(delta * 10) / 10}${unit}`,
          `⚡ Kural: ${rule.name} — cihaz çalışıyor, bekle veya manuel destekle`,
        ].join('\n');
      }

      ctx.onAlarm(msg);
      sendTelegramMessage(msg).catch(() => {});
    }

    // Koşul düzelince self-healing sayacını sıfırla
    if (!breached && state.failedRecheckCount > 0) {
      state.failedRecheckCount = 0;
      state.valueAtTrigger = null;
    }

    // İlk tetiklenme anındaki değeri kaydet (ilerleme ölçümü için)
    if (breached && state.lastTriggeredAt === now) {
      state.valueAtTrigger = getMetricValue(rule.metric, reading);
    }
  }
}

/**
 * Format an automation log entry for Telegram notification
 */
export function formatActionLogForTelegram(log: AutomationActionLog): string {
  const unit = METRIC_UNITS[log.metric];
  const label = METRIC_LABELS[log.metric];
  const actionEmoji = log.action === 'turn_on' ? '🟢' : '🔴';
  const actionText = log.action === 'turn_on' ? 'AÇILDI' : 'KAPATILDI';
  const opText = log.operator === 'above' ? '>' : '<';
  const time = new Date(log.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' });

  const lines = [
    `${actionEmoji} <b>OTOMATİK — ${log.deviceName} ${actionText}</b>`,
    '',
    `📏 ${label}: ${log.sensorValue}${unit} ${opText} ${log.threshold}${unit}`,
    `⚡ Kural: ${log.ruleName}`,
    `🕐 ${time}`,
  ];

  if (log.outcome === 'auto_off') {
    lines.push('', '⏱ Zamanlayıcı: otomatik kapandı');
  }

  return lines.join('\n');
}
